import { useCallback, useState } from "react";
import type { ShipmentDetail, ValidationErrorResponse } from "api";
import { shipmentsApi } from "api";
import axios from "axios";
import { isUnauthenticated } from "utils/error.utils";
import type { CargoForm, PayloadRows } from "./cargo.model";
import {
  gridChanged,
  IDENTITY_FIELDS,
  identityChanged,
  mapFieldErrors,
  toCargoForm,
  toCargoPayload,
  toIdentityPayload,
} from "./cargo.model";

export type CargoFormState = {
  form: CargoForm;
  update: (change: (form: CargoForm) => CargoForm) => void;
  /** Field messages keyed as `mapFieldErrors` keys them; `""` is the form-wide one. */
  errors: Record<string, string>;
  dirty: boolean;
  saving: boolean;
  /** Resolves to true once everything that changed is saved. */
  save: () => Promise<boolean>;
  /** Back to the last loaded state. */
  cancel: () => void;
};

const SAVE_FAILED = "Could not save this shipment. Please try again.";

/**
 * The Cargo tab's edits, held for the whole page: the header's Save and
 * Cancel act on them and the leave guard asks about them whichever tab is
 * open.
 *
 * Saving sends only what changed — the identity fields to `PUT
 * /shipments/{id}`, the grid to `PUT …/cargo` — because each grid save writes
 * a "Cargo details updated" row to the activity feed. The page repaints from
 * the responses rather than asking again.
 */
export const useCargoForm = (
  shipment: ShipmentDetail,
  replace: (shipment: ShipmentDetail) => void,
  onSessionLost: () => void
): CargoFormState => {
  const [initial, setInitial] = useState<CargoForm>(() => toCargoForm(shipment));
  const [form, setForm] = useState<CargoForm>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const dirty = identityChanged(form, initial) || gridChanged(form, initial);

  const fail = useCallback(
    (error: unknown, rows?: PayloadRows) => {
      if (isUnauthenticated(error)) {
        onSessionLost();
        return;
      }

      const payload = axios.isAxiosError<ValidationErrorResponse>(error) ? error.response?.data : undefined;

      if (axios.isAxiosError(error) && error.response?.status === 422 && payload?.errors) {
        setErrors(mapFieldErrors(payload.errors, rows));
        return;
      }

      setErrors({ "": SAVE_FAILED });
    },
    [onSessionLost]
  );

  const save = useCallback(async (): Promise<boolean> => {
    const sendIdentity = identityChanged(form, initial);
    const sendGrid = gridChanged(form, initial);

    if (!sendIdentity && !sendGrid) return true;

    setSaving(true);
    setErrors({});

    let current = shipment;

    try {
      if (sendIdentity) {
        const { data } = await shipmentsApi.update(shipment.id, toIdentityPayload(form));

        // The card comes back without the grid; the grid on screen still holds.
        current = { ...current, ...data };
        replace(current);

        const saved = Object.fromEntries(IDENTITY_FIELDS.map((field) => [field, form[field]]));

        setInitial((previous) => ({ ...previous, ...saved }));
      }
    } catch (error) {
      fail(error);
      setSaving(false);

      return false;
    }

    const { payload, rows } = toCargoPayload(form);

    try {
      if (sendGrid) {
        const { data } = await shipmentsApi.updateCargo(shipment.id, payload);
        const fresh = toCargoForm(data);

        replace(data);
        setInitial(fresh);
        setForm(fresh);
      }
    } catch (error) {
      fail(error, rows);
      setSaving(false);

      return false;
    }

    setSaving(false);

    return true;
  }, [fail, form, initial, replace, shipment]);

  const cancel = useCallback(() => {
    setForm(initial);
    setErrors({});
  }, [initial]);

  return { form, update: setForm, errors, dirty, saving, save, cancel };
};
